import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';

import type { CognitoUserPool } from 'amazon-cognito-identity-js';

import { PasswordInput } from '../components/password-input';
import {
  beginEmailChange,
  changeCognitoPassword,
  hasRecentAuthentication,
  verifyEmailChange,
} from '../cognito-user-api';
import { cognitoError } from '../hooks/use-session';
import type { ProfilePreferences } from '../profile-preferences';

interface ProfileSettingsProps {
  email: string;
  mfaSettings?: ReactNode;
  onProfileChange: (profile: ProfilePreferences) => void;
  pool: CognitoUserPool | undefined;
  profile: ProfilePreferences;
  accessToken?: string;
  authority?: string;
  organisationName?: string | undefined;
  membershipRoles?: string[] | undefined;
  membershipStatus?: string | undefined;
  accessibilityDisplayEnabled: boolean;
  onAccessibilityDisplayChange: (enabled: boolean) => Promise<void>;
  onOpenOrganisation?: () => void;
  onReauthenticate?: () => Promise<void>;
}

const COMMON_TIMEZONES = [
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Adelaide',
  'Australia/Perth',
  'Australia/Hobart',
  'Australia/Darwin',
  'Pacific/Auckland',
  'UTC',
] as const;

const PROFILE_ROLE_LABELS: Record<string, string> = {
  standard_user: 'Standard User',
  team_leader: 'Team Leader',
  manager: 'Manager',
  tenant_admin: 'Organisation Administrator',
  quality_manager: 'Manager (legacy)',
  auditor: 'Auditor (legacy)',
  viewer: 'Standard User (legacy)',
};

function sentenceCase(value: string): string {
  const normalized = value.replaceAll('_', ' ');
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function profileRoleLabel(role: string): string {
  return PROFILE_ROLE_LABELS[role] ?? sentenceCase(role);
}

export function ProfileSettings({
  email,
  mfaSettings,
  onProfileChange,
  pool,
  profile,
  accessToken,
  authority,
  organisationName,
  membershipRoles,
  membershipStatus,
  accessibilityDisplayEnabled,
  onAccessibilityDisplayChange,
  onOpenOrganisation,
  onReauthenticate,
}: ProfileSettingsProps) {
  const [firstName, setFirstName] = useState(profile.firstName);
  const [familyName, setFamilyName] = useState(profile.familyName);
  const [phone, setPhone] = useState(profile.phone);
  const [bio, setBio] = useState(profile.bio);
  const [pictureUrl, setPictureUrl] = useState(profile.pictureUrl);
  const [timezone, setTimezone] = useState(profile.timezone);
  const [profileMessage, setProfileMessage] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailChangeStarted, setEmailChangeStarted] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  const [emailError, setEmailError] = useState('');
  const bioRef = useRef<HTMLTextAreaElement>(null);
  const accessibilitySwitchRef = useRef<HTMLInputElement>(null);
  const [accessibilityBusy, setAccessibilityBusy] = useState(false);
  const [accessibilityMessage, setAccessibilityMessage] = useState('');
  const [accessibilityError, setAccessibilityError] = useState('');

  useEffect(() => {
    setFirstName(profile.firstName);
    setFamilyName(profile.familyName);
    setPhone(profile.phone);
    setBio(profile.bio);
    setPictureUrl(profile.pictureUrl);
    setTimezone(profile.timezone);
  }, [profile]);

  useLayoutEffect(() => {
    const textarea = bioRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [bio]);

  const availableTimezones = Array.from(new Set([timezone, ...COMMON_TIMEZONES]));

  const saveProfileValues = () => {
    const nextProfile: ProfilePreferences = {
      firstName: firstName.trim(),
      familyName: familyName.trim(),
      phone: phone.trim(),
      bio: bio.trim(),
      pictureUrl: pictureUrl.trim(),
      timezone,
    };
    onProfileChange(nextProfile);
    setProfileMessage('Profile information saved across your devices.');
  };

  const saveAccessibilityDisplay = async (enabled: boolean) => {
    if (accessibilityBusy) return;
    setAccessibilityBusy(true);
    setAccessibilityMessage('');
    setAccessibilityError('');
    try {
      await onAccessibilityDisplayChange(enabled);
      setAccessibilityMessage(
        enabled
          ? 'Enhanced accessibility display is saved and now active.'
          : 'Standard display is saved and now active.',
      );
    } catch {
      setAccessibilityError(
        'Your accessibility display preference could not be saved. The previous display has been restored.',
      );
    } finally {
      setAccessibilityBusy(false);
      window.requestAnimationFrame(() => accessibilitySwitchRef.current?.focus());
    }
  };

  const changePassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPasswordError('');
    setPasswordMessage('');
    if (newPassword.length < 14) {
      setPasswordError('Your new password must contain at least 14 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('The new passwords do not match.');
      return;
    }

    setPasswordBusy(true);
    try {
      if (accessToken && authority) {
        await changeCognitoPassword(authority, accessToken, currentPassword, newPassword);
      } else {
        const cognitoUser = pool?.getCurrentUser();
        if (!cognitoUser) throw new Error('Sign in again before changing your password.');
        await new Promise<void>((resolve, reject) =>
          cognitoUser.getSession((sessionError: Error | null) =>
            sessionError ? reject(sessionError) : resolve(),
          ),
        );
        await new Promise<void>((resolve, reject) =>
          cognitoUser.changePassword(currentPassword, newPassword, (changeError) =>
            changeError ? reject(changeError) : resolve(),
          ),
        );
      }
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordMessage('Your password has been changed.');
    } catch (error) {
      setPasswordError(cognitoError(error).message);
    } finally {
      setPasswordBusy(false);
    }
  };

  const startEmailChange = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setEmailError('');
    setEmailMessage('');
    if (!accessToken || !authority) {
      setEmailError('Sign in again before changing your email address.');
      return;
    }
    if (!hasRecentAuthentication(accessToken)) {
      setEmailError('For your security, sign in again before changing your email address.');
      return;
    }
    setEmailBusy(true);
    try {
      await beginEmailChange(authority, accessToken, newEmail.trim().toLowerCase());
      setEmailChangeStarted(true);
      setEmailMessage('Enter the verification code sent to your new email address.');
    } catch (error) {
      setEmailError(cognitoError(error).message);
    } finally {
      setEmailBusy(false);
    }
  };

  const confirmEmailChange = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!accessToken || !authority) return;
    setEmailBusy(true);
    setEmailError('');
    try {
      await verifyEmailChange(authority, accessToken, emailCode.trim());
      setEmailChangeStarted(false);
      setEmailCode('');
      setEmailMessage('Your email address is verified. Sign in again to refresh your account.');
    } catch (error) {
      setEmailError(cognitoError(error).message);
    } finally {
      setEmailBusy(false);
    }
  };

  return (
    <section aria-labelledby="profile-settings-title" className="profile-settings" id="profile">
      <div className="workspace-heading">
        <div>
          <p className="eyebrow">Profile</p>
          <h2 id="profile-settings-title">Profile settings</h2>
        </div>
        <span>Personal</span>
      </div>
      <div className="profile-settings-grid">
        <form
          className="profile-setting-card profile-information-form"
          onSubmit={(event) => {
            event.preventDefault();
            saveProfileValues();
          }}
        >
          <h3>Edit profile information</h3>
          <label>
            First name
            <input
              autoComplete="given-name"
              maxLength={80}
              required
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
            />
          </label>
          <label>
            Family name
            <input
              autoComplete="family-name"
              maxLength={80}
              value={familyName}
              onChange={(event) => setFamilyName(event.target.value)}
            />
          </label>
          <label>
            Phone
            <input
              autoComplete="tel"
              maxLength={30}
              type="tel"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
            />
          </label>
          <label>
            Bio
            <textarea
              maxLength={500}
              ref={bioRef}
              rows={3}
              value={bio}
              onChange={(event) => setBio(event.target.value)}
            />
          </label>
          <label>
            Profile image URL
            <input
              autoComplete="photo"
              inputMode="url"
              placeholder="https://example.com/profile.jpg"
              type="url"
              value={pictureUrl}
              onChange={(event) => setPictureUrl(event.target.value)}
            />
          </label>
          <span className="profile-setting-label">Signed in as {email}</span>
          <button type="submit">Save profile</button>
          {profileMessage ? (
            <p
              aria-atomic="true"
              className="profile-setting-message"
              role="status"
              aria-live="polite"
            >
              {profileMessage}
            </p>
          ) : null}
        </form>

        <section className="profile-setting-card organisation-details-card">
          <h3>Organisation details</h3>
          {organisationName ? (
            <dl>
              <dt>Organisation</dt>
              <dd>
                <a
                  className="organisation-details-link text-link"
                  href="#organisation"
                  onClick={(event) => {
                    event.preventDefault();
                    onOpenOrganisation?.();
                  }}
                >
                  {organisationName}
                </a>
              </dd>
              {membershipRoles && membershipRoles.length > 0 ? (
                <>
                  <dt>Role</dt>
                  <dd>{membershipRoles.map(profileRoleLabel).join(', ')}</dd>
                </>
              ) : null}
              {membershipStatus ? (
                <>
                  <dt>Status</dt>
                  <dd>{sentenceCase(membershipStatus)}</dd>
                </>
              ) : null}
            </dl>
          ) : (
            <p>No organisation is currently selected.</p>
          )}
        </section>

        <form
          className="profile-setting-card email-change-form"
          onSubmit={(event) =>
            void (emailChangeStarted ? confirmEmailChange(event) : startEmailChange(event))
          }
        >
          <h3>Change email</h3>
          <p>Email changes require a recent sign-in and verification of the new address.</p>
          {!emailChangeStarted ? (
            <label>
              New email
              <input
                autoComplete="email"
                disabled={emailBusy}
                required
                type="email"
                value={newEmail}
                onChange={(event) => setNewEmail(event.target.value)}
              />
            </label>
          ) : (
            <label>
              Verification code
              <input
                autoComplete="one-time-code"
                disabled={emailBusy}
                inputMode="numeric"
                required
                value={emailCode}
                onChange={(event) => setEmailCode(event.target.value.replace(/\D/g, ''))}
              />
            </label>
          )}
          <div className="email-change-actions">
            <button disabled={emailBusy} type="submit">
              {emailBusy
                ? 'Please wait...'
                : emailChangeStarted
                  ? 'Verify new email'
                  : 'Send verification code'}
            </button>
            {!emailChangeStarted && accessToken && !hasRecentAuthentication(accessToken) ? (
              <button type="button" onClick={() => void onReauthenticate?.()}>
                Sign in again
              </button>
            ) : null}
          </div>
          {emailMessage ? (
            <p
              aria-atomic="true"
              className="profile-setting-message"
              role="status"
              aria-live="polite"
            >
              {emailMessage}
            </p>
          ) : null}
          {emailError ? (
            <p className="auth-error" role="alert">
              {emailError}
            </p>
          ) : null}
        </form>

        <div className="profile-setting-card timezone-setting">
          <h3>Timezone</h3>
          <p>Dates and times in your personal workspace use this timezone.</p>
          <label>
            Timezone
            <select value={timezone} onChange={(event) => setTimezone(event.target.value)}>
              {availableTimezones.map((value) => (
                <option key={value} value={value}>
                  {value.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </label>
          <button type="button" onClick={saveProfileValues}>
            Save timezone
          </button>
        </div>

        <section
          aria-busy={accessibilityBusy}
          aria-describedby="accessibility-display-description"
          className="profile-setting-card accessibility-setting"
        >
          <h3>Accessibility</h3>
          <p id="accessibility-display-description">
            Adds stronger visual identification of links, clearer interaction indicators and reduced
            non-essential movement. Core keyboard and assistive technology support is available to
            every user.
          </p>
          <label className="accessibility-display-switch" htmlFor="accessibility-display-toggle">
            <input
              aria-describedby="accessibility-display-description accessibility-display-state"
              checked={accessibilityDisplayEnabled}
              id="accessibility-display-toggle"
              ref={accessibilitySwitchRef}
              role="switch"
              type="checkbox"
              onChange={(event) => void saveAccessibilityDisplay(event.target.checked)}
            />
            <span aria-hidden="true" className="accessibility-display-track" />
            <span>
              <strong>Enhanced accessibility display</strong>
              <small id="accessibility-display-state">
                {accessibilityDisplayEnabled
                  ? 'Enhanced accessibility display'
                  : 'Standard display'}
              </small>
            </span>
          </label>
          <button
            disabled={!accessibilityDisplayEnabled || accessibilityBusy}
            type="button"
            onClick={() => void saveAccessibilityDisplay(false)}
          >
            Reset to standard display
          </button>
          {accessibilityBusy ? (
            <p
              aria-atomic="true"
              className="profile-setting-message"
              role="status"
              aria-live="polite"
            >
              Saving accessibility display preference…
            </p>
          ) : null}
          {accessibilityMessage ? (
            <p
              aria-atomic="true"
              className="profile-setting-message"
              role="status"
              aria-live="polite"
            >
              {accessibilityMessage}
            </p>
          ) : null}
          {accessibilityError ? (
            <p className="auth-error" role="alert">
              {accessibilityError}
            </p>
          ) : null}
        </section>

        <form
          className="profile-setting-card change-password-form"
          onSubmit={(event) => void changePassword(event)}
        >
          <h3>Change password</h3>
          <label>
            Current password
            <PasswordInput
              autoComplete="current-password"
              disabled={passwordBusy}
              required
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </label>
          <label>
            New password
            <PasswordInput
              autoComplete="new-password"
              disabled={passwordBusy}
              minLength={14}
              required
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </label>
          <label>
            Confirm new password
            <PasswordInput
              autoComplete="new-password"
              disabled={passwordBusy}
              minLength={14}
              required
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
          </label>
          <button disabled={passwordBusy} type="submit">
            {passwordBusy ? 'Changing password...' : 'Change password'}
          </button>
          {passwordMessage ? (
            <p
              aria-atomic="true"
              className="profile-setting-message"
              role="status"
              aria-live="polite"
            >
              {passwordMessage}
            </p>
          ) : null}
          {passwordError ? (
            <p className="auth-error" role="alert">
              {passwordError}
            </p>
          ) : null}
        </form>

        {mfaSettings}
      </div>
    </section>
  );
}
